import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '../../lib/api';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminPagination } from '../../components/AdminPagination';
import { AdminSearchBar } from '../../components/AdminSearchBar';

export const AdminRatesPage = () => {
  const [rates, setRates] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({
    route_id: '',
    vehicle_type_id: '',
    base_price: '',
    currency: 'USD',
    is_active: true
  });

  const loadLookups = useCallback(async () => {
    try {
      const [routesRes, vehiclesRes] = await Promise.all([
        adminApi.getRoutes(1, 100),
        adminApi.getVehicles(1, 100)
      ]);
      setRoutes(routesRes.data.items || []);
      setVehicles(vehiclesRes.data.items || []);
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  }, []);

  const loadRates = useCallback(async () => {
    try {
      setLoading(true);
      const ratesRes = await adminApi.getRates(page, 20, search);
      setRates(ratesRes.data.items || []);
      setTotalPages(ratesRes.data.total_pages);
    } catch (error) {
      console.error('Error loading rates:', error);
      toast.error('Error al cargar tarifas');
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [page, search]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };
  
  const getRouteName = (routeId) => {
    const route = routes.find(r => r.id === routeId);
    return route ? `${route.from_label} → ${route.to_label}` : 'Ruta desconocida';
  };
  
  const getVehicleName = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? vehicle.name : 'Vehículo desconocido';
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        route_id: formData.route_id,
        vehicle_type_id: formData.vehicle_type_id,
        base_price: parseFloat(formData.base_price),
        currency: formData.currency,
        is_active: formData.is_active
      };
      
      if (editingRate) {
        await adminApi.updateRate(editingRate.id, data);
        toast.success('Tarifa actualizada exitosamente');
      } else {
        await adminApi.createRate(data);
        toast.success('Tarifa creada exitosamente');
      }
      
      setDialogOpen(false);
      setEditingRate(null);
      resetForm();
      loadRates();
    } catch (error) {
      console.error('Error saving rate:', error);
      toast.error('Error al guardar la tarifa');
    }
  };
  
  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      route_id: rate.route_id,
      vehicle_type_id: rate.vehicle_type_id,
      base_price: rate.base_price.toString(),
      currency: rate.currency,
      is_active: rate.is_active
    });
    setDialogOpen(true);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta tarifa?')) return;
    
    try {
      await adminApi.deleteRate(id);
      toast.success('Tarifa eliminada exitosamente');
      loadRates();
    } catch (error) {
      console.error('Error deleting rate:', error);
      toast.error('Error al eliminar la tarifa');
    }
  };
  
  const resetForm = () => {
    setFormData({
      route_id: '',
      vehicle_type_id: '',
      base_price: '',
      currency: 'USD',
      is_active: true
    });
  };
  
  if (loading && !hasLoadedRef.current) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-manrope font-bold text-3xl text-emerald-950">Tarifas</h1>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <AdminSearchBar value={search} onChange={handleSearch} placeholder="Buscar tarifa..." />
          <Button
            onClick={() => {
              resetForm();
              setEditingRate(null);
              setDialogOpen(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 shrink-0"
            data-testid="add-rate-btn"
          >
            <Plus className="w-4 h-4" />
            Agregar
          </Button>
        </div>
      </div>
      
      <div className={`bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden transition-opacity ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Ruta</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Vehículo</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Precio Base</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-slate-500">
                  {search ? `No se encontraron tarifas para "${search}"` : 'No hay tarifas registradas'}
                </td></tr>
              )}
              {rates.map((rate) => (
                <tr key={rate.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <p className="text-sm text-slate-900">{getRouteName(rate.route_id)}</p>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm font-medium text-slate-900">{getVehicleName(rate.vehicle_type_id)}</p>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-lg font-bold text-emerald-600">${rate.base_price} USD</p>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full
                      ${rate.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                      {rate.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => handleEdit(rate)}
                        className="p-2 hover:bg-slate-100 rounded transition-colors"
                      >
                        <Edit className="w-4 h-4 text-slate-600" />
                      </button>
                      <button 
                        onClick={() => handleDelete(rate.id)}
                        className="p-2 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRate ? 'Editar' : 'Agregar'} Tarifa</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Ruta *</label>
              <Select 
                value={formData.route_id} 
                onValueChange={(value) => setFormData({...formData, route_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una ruta" />
                </SelectTrigger>
                <SelectContent>
                  {routes.filter(r => r.is_active).map(route => (
                    <SelectItem key={route.id} value={route.id}>
                      {route.from_label} → {route.to_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Tipo de Vehículo *</label>
              <Select 
                value={formData.vehicle_type_id} 
                onValueChange={(value) => setFormData({...formData, vehicle_type_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un vehículo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.filter(v => v.is_active).map(vehicle => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Precio Base *</label>
                <Input 
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.base_price}
                  onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                  placeholder="45.00"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Moneda</label>
                <div className="h-10 px-3 bg-slate-100 border border-slate-200 rounded-md flex items-center text-sm text-slate-700 font-medium">
                  USD (Dólar estadounidense)
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Tarifa activa</label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {editingRate ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
